import React, { useCallback, useEffect, useRef } from 'react';
import { TextInput, View } from 'react-native';
import Text, { textVariantStyleMap } from '../../../../../v2BaseComponents/Text';
import { SetInputRefArgs } from '../../../../../v2BaseComponents/RichTable/types';
import { colors } from '../../../../../../utils/theme';
import { FixedContentContainer } from '../../../../../v2BaseComponents/FixedPositionPortalContainer';
import { useElementPosition } from '../../../../../../hooks/useElementPosition';
import { batchMeasureViewRef } from '../../../../../../utils/batchMeasureViewRef';

export type GridInputOwnProps = {
    value: string;
    onChange: (value: string) => void;
    rowIndex: number;
    columnIndex: number;
    onSetInputRef: (args: SetInputRefArgs) => void;
    error: string | null;
    container: React.MutableRefObject<View | null>;
    isDisabled: boolean;
};

export type GridInputProps = GridInputOwnProps;

type ContainerBounds = {
    top: number;
    bottom: number;
    left: number;
    right: number;
};

const GridInput: React.FC<GridInputProps> = ({
    value,
    onChange,
    rowIndex,
    columnIndex,
    onSetInputRef,
    error,
    container,
    isDisabled,
}) => {
    const [hasFocus, setHasFocus] = React.useState(false);
    const handleRef = useCallback(
        (ref: TextInput | null) => {
            onSetInputRef({ ref, rowIndex: rowIndex, columnIndex: columnIndex });
        },
        [columnIndex, onSetInputRef, rowIndex],
    );

    const displayError = hasFocus && !!error && !!container;
    const { ref, position, onLayout } = useElementPosition({
        isDisabled: !displayError,
        clearPositionOnDisable: true,
    });
    const containerBoundsRef = useRef<ContainerBounds | null>(null);
    useEffect(() => {
        if (container) {
            batchMeasureViewRef(container, (x, y, width, height, pageX, pageY) => {
                containerBoundsRef.current = { top: pageY, bottom: pageY + height, left: pageX, right: pageX + width };
            });
        }
    }, [container]);

    const isAbove = !position?.isOnTopHalfOfScreen;
    const isTextInputVisible =
        position &&
        containerBoundsRef.current &&
        position.top > containerBoundsRef.current.top &&
        position.bottom < containerBoundsRef.current.bottom &&
        position.left > containerBoundsRef.current.left &&
        position.right < containerBoundsRef.current.right;

    return (
        <View style={{ flex: 1, height: '100%', width: '100%' }} ref={ref}>
            {displayError && position && containerBoundsRef && isTextInputVisible ? (
                <FixedContentContainer>
                    <View
                        style={{
                            position: 'absolute',
                            top: isAbove ? undefined : position.topAlignFromBottom,
                            bottom: isAbove ? position.bottomAlignFromTop : undefined,
                            left: position.left - 1,
                            width: position.width + 2,
                            backgroundColor: colors.Alert,
                            padding: 4,
                            borderTopLeftRadius: isAbove ? 5 : 0,
                            borderTopRightRadius: isAbove ? 5 : 0,
                            borderBottomLeftRadius: isAbove ? 0 : 5,
                            borderBottomRightRadius: isAbove ? 0 : 5,
                        }}
                    >
                        <Text color={'White-Text'} variant="caption-1" alignText="center">
                            {error}
                        </Text>
                    </View>
                </FixedContentContainer>
            ) : null}
            <TextInput
                style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: 0,
                    borderColor: 'transparent',
                    ...({ outlineStyle: 'none' } as any),
                    padding: 4,
                    ...(textVariantStyleMap['table-content'] as any),
                }}
                value={value}
                onChange={(e) => {
                    !isDisabled && onChange(e.nativeEvent.text);
                }}
                ref={handleRef}
                onLayout={onLayout}
                onFocus={() => setHasFocus(true)}
                onBlur={() => setHasFocus(false)}
                testID={`grid-input-row-${rowIndex}-column-${columnIndex}`}
            />
        </View>
    );
};

export default React.memo(GridInput);
